import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { Query } from '@tanstack/react-query';

const STORAGE_KEY = 'nks.cache.v1';

/**
 * How long a cached read stays usable without the network.
 *
 * A working day. An advisor who opens the app on Tuesday morning in a basement
 * showroom should see Monday's list rather than an empty screen — but a list
 * two days stale is worse than no list, because they will act on it.
 */
export const OFFLINE_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/**
 * Query key prefixes that may be written to disk.
 *
 * An allowlist, not a blocklist. CLAUDE.md §5 is strict about PII, and this
 * cache lands in localStorage where any XSS can read it — the same argument
 * api-client.ts makes for keeping the access token in memory only. So the rule
 * is: persist what an advisor needs to work a pipeline with no signal, and
 * nothing else.
 *
 * What that admits: their own leads, follow-ups, quotations and bookings —
 * names, phone numbers, prices. Exactly what is already on their screen.
 *
 * What it deliberately excludes: the customer master (`customers`), which is
 * the whole book rather than the advisor's working set, the workshop job board
 * (`job-cards`) for the same reason, and anything that could carry an unmasked
 * identity number. Unmasking is a separate, audited request
 * (`customer.view_pii`), and an audited read that then sits unaudited on a
 * phone is not audited at all.
 */
const PERSISTABLE_PREFIXES = [
  'leads',
  'lead',
  'quotations',
  'quotation',
  'bookings',
  'booking',
  // A technician standing next to a motorcycle in a workshop with thick walls
  // is the case this whole mechanism was built for. Their own jobs and the one
  // they are working on — not the job board, which is the whole workshop.
  'my-jobs',
  'job-card',
];

/**
 * The second key segment of a module list read for one customer (the customer
 * page's tabs): [module, CUSTOMER_HISTORY_SCOPE, customerUid]. The module stays
 * the first segment so merges and the module's own writes still refresh it,
 * but it is never persisted: an advisor's own pipeline is their working set,
 * the history of every customer they happened to open is not (PDPA).
 */
export const CUSTOMER_HISTORY_SCOPE = 'by-customer';

export function isPersistable(query: Query): boolean {
  const first = query.queryKey[0];
  if (typeof first !== 'string') return false;
  if (query.queryKey[1] === CUSTOMER_HISTORY_SCOPE) return false;
  // Only settled, successful data. Persisting an error would replay a failure
  // from yesterday as though it were today's answer.
  if (query.state.status !== 'success') return false;
  return PERSISTABLE_PREFIXES.includes(first);
}

export const persister = createAsyncStoragePersister({
  storage: typeof window === 'undefined' ? undefined : window.localStorage,
  key: STORAGE_KEY,
  // Quota is ~5MB and shared with everything else on the origin. Failing to
  // write the cache must never break the app, so swallow and carry on.
  retry: () => undefined,
});

/**
 * Wipes the cache from disk.
 *
 * Called on sign-out. A shared showroom tablet must not hand the next advisor
 * the previous one's customer list, and "they'd have to look in devtools" is
 * not a control.
 */
export function clearOfflineCache(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private browsing, storage disabled, quota — nothing to clean up either way.
  }
}
